'use client';

import React, { useCallback, useRef, useState } from 'react';
import { IconOpenFolder, IconShare } from '@repo/svgs';
import { initChatUpload, presignedUploadHeaders, type Message } from '@repo/api';
import { UPLOAD_LIMITS } from '@repo/utils';
import { useStoreContext } from '@repo/store/web';
import { Button } from '@/components/ui';
import { useStore, type AppGlobalState } from '@/providers/StoreProvider';
import { sendDocumentUploadReply } from '@/lib/chat/chat-actions';
import { useAnalytics } from '@/hooks/useAnalytics';
import { cn } from '@/lib/cn';

const ACCEPTED_TYPES = 'application/pdf,image/png,image/jpeg,image/webp,image/heic';

type UploadStatus = 'idle' | 'uploading' | 'done' | 'error';

/**
 * Pixel dimensions for the upload init body. A `File` doesn't carry them, so
 * images are decoded to read their size; anything else has none to send.
 */
async function readImageDimensions(file: File): Promise<{ width?: number; height?: number }> {
  if (!file.type.startsWith('image/') || typeof createImageBitmap !== 'function') return {};

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    bitmap.close();
    return { width, height };
  } catch {
    return {};
  }
}

interface DocumentRequestCardProps {
  message: Message;
}

/**
 * Asks the renter for a document (e.g. a voucher letter) and uploads the file
 * the renter picks. The uploaded file isn't rendered here: the backend retracts
 * this card and sends a document_view message once the upload lands. Desktop
 * gets a drag-and-drop zone; mobile web gets a single button, since there's
 * nothing to drag from on a phone.
 */
export function DocumentRequestCard({ message }: DocumentRequestCardProps) {
  const chatDetailData = useStore(s => s.chat.chatDetailData);
  const store = useStoreContext();
  const analytics = useAnalytics();

  // Kept around so a failed upload can be retried without re-picking.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Stays busy through 'done' as well: the card lives on until the backend
  // retracts it, and picking a second file in that window would upload twice.
  const busy = status === 'uploading' || status === 'done';

  const uploadAndSend = useCallback(
    async (file: File) => {
      const chatId = chatDetailData?.chat?.id;
      if (!chatId) return;

      setStatus('uploading');
      setError(undefined);

      try {
        const mimeType = file.type || 'application/octet-stream';
        const upload = await initChatUpload({
          mimeType,
          sizeBytes: file.size,
          fileName: file.name,
          ...(await readImageDimensions(file)),
        });

        // The presigned url is S3, not our API, so it bypasses the http client.
        const uploadResponse = await fetch(upload.uploadUrl, {
          method: 'PUT',
          headers: presignedUploadHeaders(upload, mimeType),
          body: file,
        });
        if (!uploadResponse.ok) {
          throw new Error(`Upload failed (${uploadResponse.status}).`);
        }

        // The store getter is required: ChatProvider, which would set the module
        // level fallback, isn't mounted anywhere.
        await sendDocumentUploadReply(
          {
            chatId,
            ...(chatDetailData?.chat?.unit?.slug
              ? { unitSlug: chatDetailData.chat.unit.slug }
              : {}),
            uploadIds: [upload.uploadId],
            ...(message.id ? { relatedBotMessageId: message.id } : {}),
          },
          () => store.getState() as unknown as AppGlobalState,
        );

        setStatus('done');
        setPendingFile(null);
      } catch (err) {
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Could not upload that file.');
      }
    },
    [chatDetailData, message.id, store],
  );

  const attachFile = useCallback(
    (file: File) => {
      if (file.size / (1024 * 1024) > UPLOAD_LIMITS.MAX_FILE_SIZE_MB) {
        setError(`File must be ${UPLOAD_LIMITS.MAX_FILE_SIZE_MB}MB or less.`);
        return;
      }

      setError(undefined);
      setPendingFile(file);
      void uploadAndSend(file);
    },
    [uploadAndSend],
  );

  /**
   * The single funnel for opening the picker — the drop zone, its Browse files
   * button and the mobile-web button all land here, so the event fires once per
   * tap. A dropped file skips this, which is correct: it isn't a tap.
   */
  const handleBrowse = useCallback(() => {
    if (busy) return;
    void analytics.track('chat_upload', {});
    fileInputRef.current?.click();
  }, [analytics, busy]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) attachFile(file);
      // Allows re-picking the same file after a validation error.
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [attachFile],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragOver(false);
      if (busy) return;
      const file = event.dataTransfer.files?.[0];
      if (file) attachFile(file);
    },
    [attachFile, busy],
  );

  return (
    <div className="ml-10 pr-12 flex flex-col gap-2">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        onChange={handleInputChange}
        className="hidden"
      />

      {/* Desktop: drag and drop */}
      <div
        className={cn(
          'hidden md:flex w-full flex-col items-center justify-center gap-4 p-6 text-center',
          'cursor-pointer rounded-2xl border-2 border-dashed transition-colors',
          isDragOver
            ? 'border-accent bg-accent/5'
            : error
              ? 'border-alert bg-alert/5'
              : 'border-secondary bg-secondary',
        )}
        role="button"
        tabIndex={0}
        onClick={handleBrowse}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleBrowse();
          }
        }}
        onDragOver={event => {
          event.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={event => {
          event.preventDefault();
          setIsDragOver(false);
        }}
        onDrop={handleDrop}
      >
        <p className="text-body-2 text-secondary">Drag and drop a file</p>
        <p className="text-body-2 text-secondary">– or –</p>
        <Button
          variant="tertiary"
          size="md"
          onPointerDown={event => event.stopPropagation()}
          onClick={event => {
            event.stopPropagation();
            handleBrowse();
          }}
          iconLeft={<IconOpenFolder className="h-4 w-4 icon-accent" />}
          className="!text-accent !body-2-high !px-4 !py-3"
          isLoading={busy}
        >
          Browse files
        </Button>
      </div>

      {/* Mobile web: a button, nothing to drag */}
      <div
        className={cn(
          'flex md:hidden w-full items-center justify-center rounded-2xl p-6 border border-dashed border-secondary',
          error ? 'bg-alert/5' : 'bg-secondary',
        )}
        onClick={handleBrowse}
      >
        <Button
          variant="tertiary"
          size="md"
          iconLeft={<IconShare className="h-4 w-4 icon-accent" />}
          className="!text-accent !body-2-high !px-4 !py-3"
          isLoading={busy}
        >
          Upload document
        </Button>
      </div>

      {error ? (
        <div className="flex items-center gap-3">
          <p className="text-caption text-alert">{error}</p>
          {pendingFile ? (
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => void uploadAndSend(pendingFile)}
              className="!text-accent !body-2-high"
            >
              Try again
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default DocumentRequestCard;
